import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export interface LicenseCode {
  id: number;
  code: string;
  product_name: string | null;
  edition: string | null;
  seat_count: number;
  max_activations: number;
  note: string | null;
  batch_id: string | null;
  status: string;
  created_at: string;
  used_at: string | null;
}

export interface CodeExportLog {
  id: number;
  batch_id: string;
  kind: string;
  exported_count: number;
  exported_by: string | null;
  exported_by_name: string | null;
  created_at: string;
}

export interface ProductOption {
  name: string;
  editions: { key: string; label: string; price: number }[];
}

export interface GenerateCodesInput {
  productName: string;
  edition: string;
  seatCount: number;
  maxActivations: number;
  quantity: number;
  note: string;
}

export interface GenerateCodesResult {
  batchId: string;
  generated: number;
  codes: string[];
}

export interface OfflineCodeResult {
  code: string;
  machineCode: string;
  expiryCode: string;
  permanent: boolean;
}

async function invokeAdmin<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-license", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseLicenseToolsResult {
  codes: LicenseCode[];
  exportLogs: CodeExportLog[];
  products: ProductOption[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  generateCodes: (input: GenerateCodesInput) => Promise<GenerateCodesResult>;
  logCodeExport: (batchId: string, count: number, kind?: string) => Promise<void>;
  setCodeStatus: (ids: number[], status: "unused" | "disabled") => Promise<number>;
  deleteCodes: (ids: number[]) => Promise<number>;
  getOfflinePublicKey: () => Promise<string>;
  generateOfflineCode: (machineCode: string, expiresAt: string | null) => Promise<OfflineCodeResult>;
}

export function useLicenseTools(): UseLicenseToolsResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [codes, setCodes] = useState<LicenseCode[]>([]);
  const [exportLogs, setExportLogs] = useState<CodeExportLog[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setCodes([]);
      setExportLogs([]);
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [codesData, productRes] = await Promise.all([
        invokeAdmin<{ codes: LicenseCode[]; exportLogs?: CodeExportLog[] }>({ action: "listCodes" }),
        supabase.from("software_products").select("name, editions, status").order("sort_order", { ascending: true }),
      ]);
      setCodes(codesData.codes ?? []);
      setExportLogs(codesData.exportLogs ?? []);
      const productRows = (productRes.data ?? []) as Array<{
        name: string;
        editions: { key: string; label: string; price: number }[] | null;
      }>;
      setProducts(
        productRows
          .filter((row) => row.name)
          .map((row) => ({ name: row.name, editions: row.editions ?? [] })),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "授权工具数据加载失败");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const generateCodes = useCallback(
    async (input: GenerateCodesInput) => {
      const result = await invokeAdmin<GenerateCodesResult>({ action: "generateCodes", ...input });
      await load();
      return result;
    },
    [load],
  );

  // 记录一次未核销清单导出留痕（谁 / 何时 / 哪个批次 / 多少条），成功后就地刷新留痕列表。
  const logCodeExport = useCallback(
    async (batchId: string, count: number, kind = "remainder") => {
      await invokeAdmin<{ ok: boolean }>({ action: "logCodeExport", batchId, count, kind });
      await load();
    },
    [load],
  );

  const setCodeStatus = useCallback(
    async (ids: number[], status: "unused" | "disabled") => {
      const result = await invokeAdmin<{ updated: number }>({ action: "updateCodeStatus", ids, status });
      await load();
      return result.updated ?? ids.length;
    },
    [load],
  );

  const deleteCodes = useCallback(
    async (ids: number[]) => {
      const result = await invokeAdmin<{ deleted: number }>({ action: "deleteCodes", ids });
      await load();
      return result.deleted ?? ids.length;
    },
    [load],
  );

  const getOfflinePublicKey = useCallback(async () => {
    const result = await invokeAdmin<{ publicKey: string }>({ action: "getOfflinePublicKey" });
    return result.publicKey;
  }, []);

  const generateOfflineCode = useCallback(async (machineCode: string, expiresAt: string | null) => {
    return invokeAdmin<OfflineCodeResult>({ action: "generateOfflineCode", machineCode, expiresAt });
  }, []);

  return {
    codes,
    exportLogs,
    products,
    loading: loading || authLoading,
    error,
    reload: load,
    generateCodes,
    logCodeExport,
    setCodeStatus,
    deleteCodes,
    getOfflinePublicKey,
    generateOfflineCode,
  };
}